import { Badge, Box, Flex, Loader, Paper, Radio, Text } from '@mantine/core';
import { useEffect } from 'react';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { SelectableList } from 'src/UI/generate-flow/SelectableList';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  DataExportConfigOption,
  useDataExportConfigOptions,
} from '../hooks/useDataExportConfigOptions';
import { DataExportStepProps } from '../types';

export function SelectConfigStep({ data, setData }: DataExportStepProps) {
  const { configs, isLoading } = useDataExportConfigOptions();

  useEffect(() => {
    if (data.configId) return;
    const defaultConfig = configs.find(c => c.isDefault);
    if (!defaultConfig) return;
    setData(prev => ({
      ...prev,
      configId: defaultConfig.id,
      configDisplayName: defaultConfig.displayName,
    }));
  }, [configs, data.configId, setData]);

  if (isLoading) {
    return (
      <Flex direction="column" h="100%" align="center" justify="center">
        <Loader size="sm" />
      </Flex>
    );
  }

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard title="Select the configuration to use">
        <SelectableList
          items={configs}
          getKey={c => c.id}
          renderRow={config => (
            <ConfigRow
              config={config}
              selected={config.id === data.configId}
              onSelect={() =>
                setData(prev => ({
                  ...prev,
                  configId: config.id,
                  configDisplayName: config.displayName,
                }))
              }
            />
          )}
        />
      </StepCard>
    </Flex>
  );
}

type ConfigRowProps = {
  config: DataExportConfigOption;
  selected: boolean;
  onSelect: () => void;
};

function ConfigRow({ config, selected, onSelect }: ConfigRowProps) {
  return (
    <Paper
      radius="12"
      withBorder
      px="md"
      py="sm"
      role="button"
      tabIndex={0}
      onClick={onSelect}
      styles={theme => ({
        root: {
          cursor: 'pointer',
          borderColor: selected ? theme.colors.brand[6] : 'var(--default-border-color)',
          boxShadow: 'none',
          userSelect: 'none',
        },
      })}
    >
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          columnGap: 'var(--mantine-spacing-xxs)',
          alignItems: 'center',
        }}
      >
        <Flex align="center" gap="md" miw={0}>
          <Radio checked={selected} onChange={onSelect} color="brand" size="md" iconColor="white" />
          <Text fw={FONT_WEIGHTS.semibold} size="sm" truncate c="gray.8">
            {config.displayName}
          </Text>
        </Flex>
        {config.isDefault && (
          <Badge variant="light" size="sm">
            Default
          </Badge>
        )}
      </Box>
    </Paper>
  );
}
